import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { CustomLabel, CustomLegend, CustomTooltip, defaultGraphKeys } from "./CustomGrpahCommonComponents";
import { useCallback, useState } from "react";
import { Theme } from "../../Theme";

const RoundedBar = (props) => {
    const { fill, x, y, width, height } = props;

    const radius = 10;

    const safeRadius = Math.min(radius, width / 2);

    return (
        <path
            d={`M${x},${y + height} 
                   L${x},${y + safeRadius} 
                   Q${x},${y} ${x + safeRadius},${y} 
                   L${x + width - safeRadius},${y} 
                   Q${x + width},${y} ${x + width},${y + safeRadius} 
                   L${x + width},${y + height} Z`}
            stroke="none"
            fill={fill}
        />
    );
};

function CustomBarGraphChart({
    data = [],
    keys = defaultGraphKeys,
    hasLegend = true,
    hasBarLabel = false,
    customTooltip = <CustomTooltip />,
    referenceAreaFillColor = Theme.color.neutral100,
}) {
    const CHART_HEIGHT = 314; // <ResponsiveContainer>의 height
    const MARGIN_TOP = 20; // <BarChart>의 margin.top
    const MARGIN_BOTTOM = 5; // <BarChart>의 margin.bottom

    // 1. 호버된 인덱스 상태
    const [activeIndex, setActiveIndex] = useState(-1);

    // 2. 이벤트 핸들러
    const handleMouseMove = useCallback(
        (state) => {
            // state.activeTooltipIndex는 현재 호버된 데이터의 인덱스입니다.
            const newIndex = state.activeTooltipIndex;
            if (newIndex !== activeIndex) {
                setActiveIndex(newIndex);
            }
        },
        [activeIndex]
    );

    const handleMouseLeave = useCallback(() => {
        setActiveIndex(-1);
    }, []);

    // 활성화된 데이터의 X축 값 (예: '08-05')
    const activeDataName = activeIndex !== -1 ? data[activeIndex]?.name : null;

    return (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart
                data={data}
                margin={{
                    top: MARGIN_TOP,
                    right: 5,
                    left: -16,
                    bottom: MARGIN_BOTTOM,
                }}
                barSize={14}
                onMouseMove={handleMouseMove} // 마우스 움직임 감지
                onMouseLeave={handleMouseLeave} // 마우스 이탈 감지
            >
                {/* X축, Y축의 눈금선 */}
                <CartesianGrid vertical={false} stroke={Theme.color.gray150} />

                {/* X축: dataKey="name"은 data 배열 객체의 name 키를 사용합니다. */}
                <XAxis
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12, fill: Theme.color.gray400 }}
                />

                {/* Y축: 도메인(범위) */}
                <YAxis
                    domain={[0, (dataMax) => Math.ceil(dataMax / 100) * 100]}
                    tickLine={false}
                    axisLine={false}
                    tickCount={6}
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: Theme.color.gray400 }}
                />

                {/* 마우스를 올렸을 때 뜨는 툴팁 */}
                <Tooltip content={customTooltip} isAnimationActive={false} />
                {/* 하단의 범례 (Legend) */}
                {hasLegend && <Legend content={<CustomLegend />} />}

                {activeDataName && (
                    <ReferenceArea
                        x1={activeDataName}
                        x2={activeDataName}
                        fill={referenceAreaFillColor} // 이미지와 비슷한 회색 계열
                        fillOpacity={1} // 투명도
                    />
                )}

                {/* 각 데이터 시리즈에 대한 막대 그래프 */}
                {keys.map((key, index) => (
                    <Bar
                        key={`bar-${index}`}
                        dataKey={key.name}
                        fill={key.color}
                        shape={<RoundedBar />}
                        label={hasBarLabel ? <CustomLabel /> : null}
                        animationDuration={0}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

export default CustomBarGraphChart;
